<template>
  <div class="app-select-field">
    <div v-if="label" class="field-label">
      <v-icon v-if="labelIcon" size="18">{{ labelIcon }}</v-icon>
      <span>{{ label }}</span>
    </div>
    <v-select
      v-model="selectModel"
      :items="items"
      :item-title="itemTitle"
      :item-value="itemValue"
      :menu-props="menuProps"
      :placeholder="placeholder"
      persistent-placeholder
      :class="fieldClass"
      :variant="variant"
      clearable
      :flat="flat"
      :hide-details="hideDetails"
      v-bind="fieldAttrs"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue";

defineOptions({
  inheritAttrs: false,
});

const model = defineModel<unknown>();

const props = withDefaults(
  defineProps<{
    items: Array<string | Record<string, unknown>>;
    menuProps?: Record<string, unknown>;
    itemTitle?: string;
    itemValue?: string;
    variant?:
    | "solo"
    | "filled"
    | "outlined"
    | "plain"
    | "solo-filled"
    | "solo-inverted"
    | "underlined";
    flat?: boolean;
    hideDetails?: boolean | "auto";
    label?: string;
    labelIcon?: string;
    placeholder?: string;
  }>(),
  {
    variant: "solo",
    flat: true,
    hideDetails: "auto",
    label: "",
    labelIcon: "",
    placeholder: "Select option",
    itemTitle: "title",
    itemValue: "value",
    menuProps: undefined,
  },
);

const attrs = useAttrs();
const selectModel = computed({
  get: () => (model.value === "" ? null : model.value),
  set: (value) => {
    model.value = value ?? "";
  },
});
const fieldClass = computed(() => ["app-select-field__input", attrs.class]);
const menuProps = computed(() => {
  const extra = props.menuProps ?? {};
  const contentClass = [
    "app-select-menu",
    (extra as { contentClass?: string }).contentClass,
  ]
    .filter(Boolean)
    .join(" ");
  return { ...extra, contentClass };
});

const fieldAttrs = computed(() => {
  const { class: _class, ...rest } = attrs;
  return rest;
});
</script>

<style scoped>
.app-select-field {
  width: 100%;
}

.field-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-subtitle);
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 6px;
  padding: 0 10px;
}

.app-select-field__input {
  --v-input-control-height: 48px;
  --v-input-padding-top: 0px;
  --v-field-padding-top: 0px;
  --v-field-padding-bottom: 0px;
  --v-field-input-padding-top: 0px;
  --v-field-input-padding-bottom: 0px;
  --v-field-padding-start: 12px;
  --v-field-padding-end: 12px;
}

.app-select-field__input :deep(.v-field) {
  border: 1px solid #eaeaea;
}

.app-select-field__input :deep(.v-field__input) {
  font-size: 0.875rem;
}

.app-select-field__input :deep(.v-input--density-default .v-field--variant-outlined),
.app-select-field__input :deep(.v-input--density-default .v-field--single-line),
.app-select-field__input :deep(.v-input--density-default .v-field--no-label) {
  --v-field-padding-bottom: 0px !important;
}

.app-select-field__input :deep(.v-input--density-default .v-field--variant-solo),
.app-select-field__input :deep(.v-input--density-default .v-field--variant-solo-inverted),
.app-select-field__input :deep(.v-input--density-default .v-field--variant-solo-filled),
.app-select-field__input :deep(.v-input--density-default .v-field--variant-filled) {
  --v-input-control-height: 48px;
  --v-field-padding-bottom: 0px !important;
}

/* :global(.app-select-menu) {
  box-shadow: 0 6px 14px rgba(221, 221, 223, 0.12);
} */
.app-select-field__input :deep(.v-field__input)>input {
  top: 13px !important;
}
.v-list {
    border-color: rgb(255 255 255) !important;
}
</style>
